import { FC, useMemo, useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, List, ListItem, useTheme, useMediaQuery } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { formatDistance } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import CircleIcon from '@mui/icons-material/Circle';

import { useAuth, useChat } from '@carcareers/contexts';
import { routes } from '@carcareers/utils';
import { ChatListLoader } from '@carcareers/components';

export const ChatRoomList: FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const { chatRooms, selectedRoom, loadingRooms, hasMoreRooms, loadChatRooms } = useChat();
  const chatRoomsLength = useMemo(() => Object.keys(chatRooms).length, [chatRooms]);
  const { user } = useAuth();

  const isEmployer = user?.role === 'employer';
  
  // Sort chat rooms by last message timestamp (newest first)
  const sortedChatRooms = useMemo(() => {
    return Object.values(chatRooms).sort((a, b) => {
      const timeA = a.last_message_sent_at ? new Date(a.last_message_sent_at).getTime() : 0;
      const timeB = b.last_message_sent_at ? new Date(b.last_message_sent_at).getTime() : 0;
      return timeB - timeA;
    });
  }, [chatRooms]);

  // Check if we need to load more data on larger screens
  useEffect(() => {
    if (isLargeScreen && hasMoreRooms && chatRoomsLength > 0 && chatRoomsLength < 15) {
      loadChatRooms();
    }
  }, [isLargeScreen, chatRoomsLength, hasMoreRooms, loadChatRooms]);

  // Set initial loading state
  useEffect(() => {
    if (loadingRooms && chatRoomsLength === 0) {
      setIsInitialLoading(true);
    } else {
      setIsInitialLoading(false);
    }
  }, [loadingRooms, chatRoomsLength]);

  if (isInitialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isInitialLoading && chatRoomsLength === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>No conversations yet.</Typography>
      </Box>
    );
  }

  return (
    <Box
      id="chatRoomScrollable"
      sx={{
        bgcolor: 'background.paper',
        height: '100%',
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'rgba(0,0,0,0.1)',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.3)',
          borderRadius: '2px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
      }}
    >
      <InfiniteScroll
        dataLength={chatRoomsLength}
        next={loadChatRooms}
        hasMore={hasMoreRooms}
        scrollableTarget="chatRoomScrollable"
        loader={<ChatListLoader />}
        endMessage={
          chatRoomsLength === 0 ? (
            <Box sx={{ textAlign: 'center', p: 2 }}>
              <Typography>No conversations yet.</Typography>
            </Box>
          ) : null
        }
      >
        <List disablePadding>
          {sortedChatRooms.map((room) => {
            const hasUnread = isEmployer ? room.employer_has_unread : room.jobseeker_has_unread;
            const lastMessageTime = room.last_message_sent_at
              ? formatDistance(new Date(room.last_message_sent_at), new Date(), { addSuffix: true })
              : 'No messages yet';

            const applicantName = room.jobseeker_name;
            const employerName = room.employer_name;
            const roomRoute = isEmployer
              ? `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_CHAT}`
              : `/${routes.JOBSEEKER_HOME}/${routes.JOBSEEKER_CHAT}`;

            return (
              <ListItem
                key={room.id}
                onClick={() => navigate(`${roomRoute}/${room.id}`)}
                sx={{
                  p: 2,
                  borderBottom: '1px solid #e0e0e0',
                  borderLeft: '4px solid transparent',
                  borderLeftColor: room.id === selectedRoom?.id ? '#2196f3' : 'transparent',
                  transition: 'all 0.1s ease',
                  '&:hover': {
                    borderLeft: '4px solid #2196f3',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  display: 'flex',
                  gap: 2,
                }}
              >
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ flexGrow: 1, minWidth: 0, mr: 1 }} variant="subtitle1" noWrap>
                      {isEmployer ? applicantName : employerName}
                    </Typography>
                    <Typography sx={{ minWidth: '120px', textAlign: 'right' }} variant="caption" color="text.secondary">
                      {hasUnread ? <CircleIcon sx={{ fontSize: '12px', color: 'primary.main' }} /> : lastMessageTime}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    color={hasUnread ? 'text.primary' : 'text.secondary'}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: hasUnread ? 600 : 400,
                    }}
                  >
                    {room.last_message_text || 'No messages yet'}
                  </Typography>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {room.job_title}
                  </Typography>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </InfiniteScroll>
    </Box>
  );
};

export default ChatRoomList;
